import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Datasets = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch datasets from Hugging Face API
  useEffect(() => {
    const fetchDatasets = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch datasets from VossMoos organization
        const response = await fetch('https://huggingface.co/api/datasets?author=vossmoos&limit=10');
        
        if (!response.ok) {
          throw new Error('Failed to fetch datasets');
        }
        
        const data = await response.json();
        
        // Transform Hugging Face data to our format
        const transformedDatasets = data.map((dataset, index) => {
          // Extract dataset name from the ID (remove author prefix)
          const datasetName = dataset.id ? dataset.id.split('/').pop() : 'Untitled Dataset';
          
          // Clean and truncate description
          const cleanDescription = dataset.description 
            ? dataset.description
                .replace(/\n\t+/g, ' ') // Remove newlines and tabs
                .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                .trim()
                .substring(0, 200) + (dataset.description.length > 200 ? '...' : '')
            : 'Dataset description not available';
          
          // Use tags as features directly from API
          const features = dataset.tags || [];
          
          // Use size category from tags if available
          const sizeCategory = dataset.tags?.find(tag => tag.startsWith('size_categories:'))?.split(':')[1] || 'Unknown';
          
          // Use tags as use cases directly from API
          const useCases = dataset.tags || [];
          
          return {
            id: dataset._id || dataset.id || index + 1,
            title: datasetName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            description: cleanDescription,
            features: features,
            size: sizeCategory,
            accuracy: dataset.tags?.includes('synthetic') ? "Synthetic Data" : "Real Data",
            useCases: useCases,
            color: index % 2 === 0 ? "from-blue-500 to-cyan-500" : "from-green-500 to-emerald-500",
            icon: index % 2 === 0 ? "🌪️" : "🌱",
            huggingfaceUrl: `https://huggingface.co/datasets/${dataset.id}`,
            lastUpdated: dataset.lastModified ? new Date(dataset.lastModified).toLocaleDateString() : "Unknown",
            downloads: dataset.downloads ? dataset.downloads.toString() : "0"
          };
        });
        
        setDatasets(transformedDatasets);
      } catch (err) {
        console.error('Error fetching datasets:', err);
        setError('Failed to load datasets. Please try again later.');
        
        // Fallback to empty array or show error state
        setDatasets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDatasets();
  }, []);

  const handleDownloadDataset = async (datasetUrl) => {
    setLoading(true);
    try {
      // Open the Hugging Face dataset page in a new tab
      window.open(datasetUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening dataset:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section bg-background-secondary" id="datasets">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          viewport={{ once: true }}
        >

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Data Repository
          </h2>
          
          <p className="text-lg text-foreground-muted max-w-3xl mx-auto leading-relaxed">
            Examples of datasets generated by Simulated Experience Studio are publicly available on Hugging Face and Kaggle.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            <span className="ml-4 text-foreground-muted">Loading datasets...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <div className="text-red-500 mb-4">{error}</div>
            <button 
              onClick={() => window.location.reload()} 
              className="btn btn-outline"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Datasets Grid */}
        {!loading && !error && (
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {datasets.map((dataset, index) => (
            <motion.div
              key={dataset.id}
              className="card-elevated group cursor-pointer p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0, 0.2, 1] }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Header */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-foreground mb-4">{dataset.title}</h3>
                <div className="flex items-center space-x-4 text-sm text-foreground-muted">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-success rounded-full mr-2"></span>
                    {dataset.accuracy}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                    {dataset.size}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-accent rounded-full mr-2"></span>
                    {dataset.downloads} downloads in the current month
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className="text-foreground-muted mb-8 leading-relaxed">
                {dataset.description}
              </p>


              {/* Use Cases */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-foreground mb-4">Use Cases</h4>
                <div className="flex flex-wrap gap-3">
                  {dataset.useCases.map((useCase, useCaseIndex) => (
                    <span 
                      key={useCaseIndex}
                      className="px-3 py-1 bg-accent/10 text-accent text-sm rounded-full border border-accent/20"
                    >
                      {useCase}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button 
                  className="btn btn-primary w-full sm:w-auto"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDownloadDataset(dataset.huggingfaceUrl)}
                  disabled={loading}
                >
                  {loading ? 'Opening...' : 'View on Hugging Face'}
                </motion.button>
                <motion.button 
                  className="btn btn-outline w-full sm:w-auto"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDownloadDataset('https://www.kaggle.com/vossmoos/datasets')}
                  disabled={loading}
                >
                  View on Kaggle
                </motion.button>
              </div>
            </motion.div>
            ))}
          </div>
        )}

      </div>
    </section>
  );
};

export default Datasets;
